import React from "react";
import { Link } from "react-router-dom";
import { SUPPORT_EMAIL, SUPPORT_MAILTO } from "../../lib/legalCopy";

/**
 * Shared chrome for the public, no-auth-required legal/marketing pages
 * (`/about`, `/terms`, `/privacy`). Stripe’s review team must be able to
 * land on these URLs from a logged-out browser session and immediately see:
 *
 *   - what TipLingo is and who runs it,
 *   - cross-links between the legal pages,
 *   - a clickable `mailto:` for support.
 *
 * Visually we mirror the v2 in-app system (bone background, navy headings,
 * orange accents, Jakarta typography) so external visitors recognise it as
 * the same product they’ll see inside the app.
 */
export default function PublicLegalLayout({ title, children }) {
  return (
    <div className="min-h-screen bg-bone text-navy">
      {/* Sticky brand bar — always visible so reviewers know what site they’re on. */}
      <header className="bg-white border-b border-navy/10 shadow-soft sticky top-0 z-20">
        <div className="max-w-3xl mx-auto px-5 py-3 flex items-center justify-between gap-3">
          <Link
            to="/"
            data-testid="public-brand-link"
            className="font-jakarta font-extrabold text-2xl text-navy leading-none"
          >
            Tip<span className="text-orange">Lingo</span>
          </Link>
          <nav className="flex items-center gap-1 text-xs font-jakarta font-bold uppercase tracking-wider">
            <Link
              to="/about"
              data-testid="public-nav-about"
              className="px-3 py-2 rounded-full text-navy-soft hover:text-navy hover:bg-bone"
            >
              About
            </Link>
            <Link
              to="/terms"
              data-testid="public-nav-terms"
              className="px-3 py-2 rounded-full text-navy-soft hover:text-navy hover:bg-bone"
            >
              Terms
            </Link>
            <Link
              to="/privacy"
              data-testid="public-nav-privacy"
              className="px-3 py-2 rounded-full text-navy-soft hover:text-navy hover:bg-bone"
            >
              Privacy
            </Link>
            <Link
              to="/support"
              data-testid="public-nav-support"
              className="ml-1 px-3 py-2 rounded-full bg-orange text-white hover:bg-orange-hover"
            >
              Support
            </Link>
          </nav>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 py-10">
        <h1
          data-testid="public-page-title"
          className="font-jakarta font-extrabold text-4xl sm:text-5xl text-navy leading-tight mb-8"
        >
          {title}
        </h1>
        <article className="space-y-6 font-jakarta text-navy text-base leading-relaxed">
          {children}
        </article>
      </main>

      <footer className="border-t border-navy/10 bg-white mt-12">
        <div className="max-w-3xl mx-auto px-5 py-6 text-xs font-jakarta text-navy-soft flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div>© {new Date().getFullYear()} TipLingo · Your travel companion.</div>
          <div className="flex items-center gap-3">
            <Link to="/about" className="hover:text-navy">About</Link>
            <Link to="/terms" className="hover:text-navy">Terms</Link>
            <Link to="/privacy" className="hover:text-navy">Privacy</Link>
            <a href={SUPPORT_MAILTO} className="text-orange font-bold hover:underline">
              {SUPPORT_EMAIL}
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
